/* GASSISA
=========================================================================================
* Descripción: Módulo de entradas de inventario por Almacén o Exhibidor
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 29/01/2025 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 05/05/2026 | Refactor completo                | pcTrabajo, edición qty, cleanup
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Búsqueda multi almacén de artículos
*      4       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Bloqueo de buscador si Nueva esté activo
*      5       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Consulta folio de entrada al cambiar de ventana
*      6       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Retiro de atajos directos de eliminación
 *      7       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Habilitación del lector al iniciar captura sin partidas
 *      8       | 30/08/2026 | Lic. José Luis Aragón Cervantes  | Alt+F12 elimina todo guardando primero la entrada con confirmación
 *      9       | 23/09/2026 | LIC. José Luis Aragón C.                       | Lector estilo Conteos + captura en
 *              |            |                                  | buffer Redis (fuera localStorage)
 *      10      | 23/09/2026 | LIC. José Luis Aragón C.                       | Autofinado periódico silencioso
 *              |            |                                  | + finado al cerrar pestaña
 *      11      | 23/09/2026 | LIC. José Luis Aragón C.                       | Alt+F1 repetible con reintentos;
 *              |            |                                  | modal visible aunque sea EDITAR
 * ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Box, IconButton } from "@mui/material";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { copiarTexto } from "../../helpers/portapapeles";

import { getDateStringV2 } from "../../settings/utils.js";
import { apiEntradasGuardarTemporales } from "../../settings/ApiConfig";

import MnuEntradasAlmacenesExhibidoresActions from "./MnuEntradasAlmacenesExhibidoresActions";
import SizeSoftXLectorCodigoBarrasEntradas from "../leyout/SizeSoftXLectorCodigoBarrasEntradas";
import ModalBuscarArticulosParaVenta from "../herramientas/ModalBuscarArticulosParaVenta";
import ModalBuscarEntradasSalidas from "../herramientas/ModalBuscarEntradasSalidas";
import SizeSoftXConfirmDialog from "../leyout/SizeSoftXConfirmDialog";
import SizeSoftXAlert from "../utils/SizeSoftXAlert";
import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm";
import SizeSoftXTableVentas from "../leyout/SizeSoftXTableVentas";
import SemaforoStatus from "../utils/SemaforoStatus";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess";

import { useInventarios } from "../../context/Inventarios/InventariosContext";
import { useSesion } from "../../context/System/SesionContext";
import { useFormTabLock } from "../../hooks/useFormTabLock";
import EntradasLocalService from "../../services/EntradasLocalService";

// ─── Subcomponente: Celda de cantidad editable ──────────────────────────────────
// Encapsula su propio estado local para evitar renders cruzados y stale data.
function CantidadCell({ row, indice, onSave, onNavigate, disabled }) {
  const [valor, setValor] = useState(String(row.cantidadarticulo));
  const inputRef = useRef(null);

  // Sincronizar cuando el row cambie externamente (ej. después de guardar)
  useEffect(() => {
    setValor(String(row.cantidadarticulo));
  }, [row.cantidadarticulo, row.id]);

  const handleBlur = async () => {
    const nuevaCantidad = parseInt(valor, 10);
    if (valor !== "" && !isNaN(nuevaCantidad) && nuevaCantidad > 0 && nuevaCantidad !== row.cantidadarticulo) {
      const cantidadFinal = await onSave(row, nuevaCantidad);
      if (cantidadFinal) setValor(String(cantidadFinal));
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      const nuevaCantidad = parseInt(valor, 10);
      if (valor !== "" && !isNaN(nuevaCantidad) && nuevaCantidad > 0 && nuevaCantidad !== row.cantidadarticulo) {
        const cantidadFinal = await onSave(row, nuevaCantidad);
        if (cantidadFinal) setValor(String(cantidadFinal));
      }
      onNavigate(row, e.key === "ArrowUp" ? -1 : 1, indice);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setValor(String(row.cantidadarticulo));
      return;
    }
  };

  if (disabled) {
    return (
      <TextField autoComplete="off"
        size="small"
        value={row.cantidadarticulo}
        disabled
        inputProps={{
          style: { textAlign: "center", padding: "4px 2px", width: "60px" },
        }}
      />
    );
  }

  return (
    <TextField autoComplete="off"
      size="small"
      data-cantidad-id={row.id}
      inputRef={inputRef}
      value={valor}
      onClick={(e) => {
  e.stopPropagation();
  e.target.select();
}}
      onFocus={(e) => {
  e.stopPropagation();
  setValor(String(row.cantidadarticulo));
  setTimeout(() => e.target.select(), 0);
}}
      onChange={(e) => setValor(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      inputProps={{
        min: 1,
        inputMode: "numeric",
        style: { textAlign: "center", padding: "4px 2px", width: "60px" },
      }}
    />
  );
}

// ─── Componente principal ────────────────────────────────────────────────────────
export default function FrmEntradasAlmacenesExhibidores() {
  const { isLocked: isFormLocked } = useFormTabLock('entradas_almacenes', {
    title: 'Entradas Almacenes',
    onLockDetected: (info) => {
      console.warn(`El formulario de ${info?.title || 'Entradas Almacenes'} ya está abierto en otra pestaña`);
    }
  });

  const {
    folioEntrada,
    handleObtenerDatosEntradas,
    handleCargarDatosEntradaOtraPC,
    handleObtenerAlmacenesExhibidores,
    handleClickGuaredarEntrada,
    handleClickNuevaEntradaButton,
    listaConceptosEntrada,
    listaAlmacenesExhibidoresEntrada,
    totalPMVB,
    totalCostoVenta,
    totalVentaNeta,
    totalRegistros,
    cantidadArticulos,
    statusModalBuscadorArticulos,
    conceptoEntrada,
    handleChangeConcepto,
    handleChangeAlmacenexhibidor,
    almacenExhibidor,
    handleObtenerFolioEntradaDisponible,
    statusOpenDialog,
    titleOpenDialog,
    messageOpenDialog,
    handleCloseBuscarArticuloDescripcion,
    hendleEliminarPartidaEntradaSalida,
    handleObtenerArticuloDataFull,
    openModal,
    setOpenModal,
    pdfUrl,
    handleCloseModarBuscarEntradaSalida,
    openModarlBuscarEntradaSalida,
    statusWaiting,
    listaEntradas,
    handleClickRegresarMainDashboard,
    handleBuscarArticuloDescripcion,
    handleAplicarEntrada,
    handleImprimirEntrada,
    handleEliminarDataEntrada,
    titleActionSave,
    handleObtenerConceptosEntradas,
    handleActualizarCantidadPartidaEntrada,
    statusEntrada,
    openModalBuscarArticulos,
    setOpenModalBuscarArticulos,
    setVentanaAbierta,
    ventanaAbierta,
    hendleEliminarEntradaSalida,
    // Rev 9 (23/09/2026): flujo Redis (catélogo oculto + buffer, vía RequestContext/JWE)
    handleCargarCatalogoCbarras,
    resolverArticuloCbarrasLocal,
    handleResolverCodigoEntradas,
    handleCapturarEntradaRedis,
    handleFlushEntradaRedis,
    handleCargarEntradasRedis,
  } = useInventarios();

  const { parametrosCajaForm, tipoUsuario } = useSesion();

  const [partidaSeleccionada, setPartidaSeleccionada] = useState({});

  useEffect(() => {
    if (isFormLocked) {
      alert("El formulario de Entradas Almacenes ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
      setTimeout(() => {
        handleClickRegresarMainDashboard();
      }, 900);
    }
  }, [isFormLocked]);

  // ─── Sin scroll general: solo desplaza la tabla ──────────────────────────
  // Bloquea el overflow del contenedor del dashboard mientras este módulo esté
  // activo, para que exista una sola barra vertical (la del detalle).
  useEffect(() => {
    const el = document.querySelector('.sizesoftx-dashboard .content');
    if (!el) return;
    const prevY = el.style.overflowY;
    const prevX = el.style.overflowX;
    el.style.overflowY = 'hidden';
    el.style.overflowX = 'hidden';
    return () => {
      el.style.overflowY = prevY;
      el.style.overflowX = prevX;
    };
  }, []);

  // ─── Rev 9 (23/09/2026): carga oculta del catélogo de códigos ──────────────
  // Llena el Map local y siembra Redis en backend (por chunks, sin loader).
  useEffect(() => {
    handleCargarCatalogoCbarras().catch(() => {});
  }, []);

  // ─── PC de trabajo: siempre usar valor actual de parametrosCajaForm ───────────
  const pcAnfitrionaRef = useRef(parametrosCajaForm.caja?.trim().toUpperCase() || "");
  
  const getPcAnfitriona = () => parametrosCajaForm.caja?.trim().toUpperCase() || "";
  const [pcTrabajo, setPcTrabajo] = useState(getPcAnfitriona());

  // Actualizar pcAnfitrionaRef cuando parametrosCajaForm.caja cambia
  useEffect(() => {
    pcAnfitrionaRef.current = getPcAnfitriona();
  }, [parametrosCajaForm.caja]);

  const esEntradaDeOtraPC = pcTrabajo && pcTrabajo !== pcAnfitrionaRef.current && pcAnfitrionaRef.current !== "CAJA-DEFAULT";

  // ─── Estados locales de UI ─────────────────────────────────────────────────
  const [talla, setTalla] = useState(0);
  const mostrarBuscadorArticulosRef = useRef(false);
  const ultimoArticuloRegistradoRef = useRef(null);
  const tableRef = useRef(null);
  const origenScannerRef = useRef(false);
  const cargandoDesdeModalRef = useRef(false);
  const nuevaEntradaEnProcesoRef = useRef(false);
  const sortedDataRef = useRef([]);
  const ultimaEntradaCargadaRef = useRef({ folio: null, pc: null });

  // ─── Índice explícito de navegación: posición en el orden visible ──────────
  // Se reindexa en cada reordenamiento/carga para que las flechas avancen 1 a 1.
  const posNavRef = useRef(-1);
  const ordenVisible = () => {
    const lista = Array.isArray(listaEntradas) ? listaEntradas : [];
    return sortedDataRef.current.length > 0 ? sortedDataRef.current : lista;
  };
  const aplicarOrdenVisible = (sorted) => {
    const orden = Array.isArray(sorted) ? sorted : [];
    sortedDataRef.current = orden;
    if (orden.length === 0) { posNavRef.current = -1; return; }
    const idx = orden.findIndex((r) => r && partidaSeleccionada && r.id === partidaSeleccionada.id);
    posNavRef.current = idx >= 0 ? idx : 0;
  };

  // Concepto Autocomplete
  const [conceptoSeleccionado, setConceptoSeleccionado] = useState(null);
  const [conceptoInput, setConceptoInput] = useState("");

  // Almacén Autocomplete
  const [almacenSeleccionado, setAlmacenSeleccionado] = useState(null);
  const [almacenInput, setAlmacenInput] = useState("");
  const [almacenError, setAlmacenError] = useState(false);
  const [openConfirmEliminarEntrada, setOpenConfirmEliminarEntrada] = useState(false);

  // ─── Listas ordenadas para los Autocomplete ─────────────────────────────────
  const listaConceptosOrdenada = useMemo(
    () => (listaConceptosEntrada ?? []).slice().sort((a, b) => a.concepto.localeCompare(b.concepto)),
    [listaConceptosEntrada]
  );

  const listaAlmacenesExhibidoresOrdenada = useMemo(
    () =>
      (listaAlmacenesExhibidoresEntrada ?? [])
        .slice()
        .sort((a, b) => a.almacenexhibidor.localeCompare(b.almacenexhibidor)),
    [listaAlmacenesExhibidoresEntrada]
  );

  const listaConceptosAutoComplete = useMemo(
    () => listaConceptosOrdenada.map((item) => ({ id: item.id, concepto: item.concepto })),
    [listaConceptosOrdenada]
  );

  const listaAlmacenesExhibidoresAutoComplete = useMemo(
    () => listaAlmacenesExhibidoresOrdenada.map((item) => ({ id: item.id, nombre: item.almacenexhibidor })),
    [listaAlmacenesExhibidoresOrdenada]
  );

  // ─── Derivados ─────────────────────────────────────────────────────────────
  const tieneRegistros = Array.isArray(listaEntradas) && listaEntradas.length > 0;
  const totalesLocal = useMemo(() => {
    const lista = Array.isArray(listaEntradas) ? listaEntradas : [];
    return {
      registros: lista.length,
      articulos: lista.reduce((s, i) => s + (parseFloat(i.cantidadarticulo) || 0), 0),
      pmvb: lista.reduce((s, i) => s + (parseFloat(i.cantidadarticulo) || 0) * (parseFloat(i.pmvb) || 0), 0),
    };
  }, [listaEntradas]);
  const statusEntradaNormalizado = (statusEntrada || "").trim().toUpperCase();
  const statusPartidaCabecera = (
    (listaEntradas && listaEntradas[0])?.statusentradasalida ||
    (listaEntradas && listaEntradas[0])?.sStatusEntradaSalida ||
    ""
  ).trim().toUpperCase();
  const movimientoBloqueado =
    statusEntradaNormalizado === "TR" ||
    statusEntradaNormalizado === "CA" ||
    statusPartidaCabecera === "TR" ||
    statusPartidaCabecera === "CA";
  const modoEdicionEntrada = titleActionSave === "GUARDAR";
  const funcionalidadBloqueada = movimientoBloqueado || !modoEdicionEntrada;
  const buscadorArticulosBloqueado = funcionalidadBloqueada;
  const lectorCodigoBarrasBloqueado = funcionalidadBloqueada;
  const puedeAplicar = tieneRegistros && !movimientoBloqueado && modoEdicionEntrada;
  const estaPartidaBloqueada = (row) => {
    const statusPartida = (row?.statusentradasalida || row?.sStatusEntradaSalida || "").trim().toUpperCase();
    return movimientoBloqueado || statusPartida === "TR" || statusPartida === "CA";
  };

  // ─── Sync concepto desde contexto → autocomplete local ──────────────────────
  useEffect(() => {
    if (!conceptoEntrada) {
      setConceptoSeleccionado(null);
      setConceptoInput("");
      return;
    }
    const found = listaConceptosAutoComplete.find(
      (item) => item.concepto === conceptoEntrada
    ) || null;
    setConceptoSeleccionado(found);
    setConceptoInput(found ? found.concepto : conceptoEntrada);
  }, [conceptoEntrada, listaConceptosAutoComplete]);

  // ─── Sync almacén desde contexto → autocomplete local ───────────────────────
  useEffect(() => {
    if (!almacenExhibidor) {
      setAlmacenSeleccionado(null);
      setAlmacenInput("");
      return;
    }
    const found = listaAlmacenesExhibidoresAutoComplete.find(
      (item) => item.nombre === almacenExhibidor
    ) || null;
    setAlmacenSeleccionado(found);
    setAlmacenInput(found ? found.nombre : almacenExhibidor);
    setAlmacenError(false);
  }, [almacenExhibidor, listaAlmacenesExhibidoresAutoComplete]);

  // ─── Init y cleanup (unmount → restaurar PC anfitriona) ─────────────────────
  useEffect(() => {
    if (listaConceptosEntrada.length === 0) {
      handleObtenerConceptosEntradas();
    }
    if (listaAlmacenesExhibidoresEntrada.length === 0) {
      handleObtenerAlmacenesExhibidores("ENTRADA");
    }

    const pending = EntradasLocalService.cargar();
    const pendingFolio = pending && pending.length > 0 ? pending[0].folioentradasalida : null;

    if (ventanaAbierta !== "ENTRADA") {
      setVentanaAbierta("ENTRADA");
      setPcTrabajo(getPcAnfitriona());
      if (pendingFolio) {
        handleObtenerDatosEntradas(pendingFolio);
      } else {
        handleObtenerFolioEntradaDisponible();
      }
      return;
    }

    if (folioEntrada === 0 && !pendingFolio) {
      handleObtenerFolioEntradaDisponible();
      return;
    }

    if (folioEntrada > 0 && !cargandoDesdeModalRef.current && !nuevaEntradaEnProcesoRef.current) {
      // Si el folio ya se cargó explícitamente desde el modal (misma PC), no recargar:
      // evita que la recarga automética con la PC local pise los datos de otra PC.
      // Rev 9: lista Redis (SQL finado + buffer pendiente, recupera sesión previa).
      const ultima = ultimaEntradaCargadaRef.current;
      if (!(ultima.folio === folioEntrada && ultima.pc === pcTrabajo)) {
        handleCargarEntradasRedis(folioEntrada, pcTrabajo);
      }
      return;
    }
  }, [folioEntrada, pcTrabajo, ventanaAbierta]);

  // ─── Selección desde modal de búsqueda de entradas ──────────────────────────
  // Rev 9 (23/09/2026): antes de cambiar de folio se fina el buffer en SQL.
  const handleSelectEntradaConValidacionPC = async (entrada) => {
    await guardarEntradaSiHayCambios();
    const pcEntrada = entrada?.pc ? String(entrada.pc).trim().toUpperCase() : "";
    const pcSeleccionada = pcEntrada || getPcAnfitriona();
    
    setPcTrabajo(pcSeleccionada);
    cargandoDesdeModalRef.current = true;

    if (pcEntrada && pcEntrada !== getPcAnfitriona() && getPcAnfitriona() !== "CAJA-DEFAULT") {
      await handleCargarDatosEntradaOtraPC(entrada.folio, entrada.pc, entrada.concepto, entrada.almacenexhibidor);
    } else {
      await handleObtenerDatosEntradas(entrada.folio, entrada.concepto, entrada.almacenexhibidor, pcEntrada || getPcAnfitriona());
    }

    cargandoDesdeModalRef.current = false;
    ultimaEntradaCargadaRef.current = { folio: entrada.folio, pc: pcSeleccionada };
    refocusBarcode();
  };

  // ─── Rev 9 (23/09/2026): escaneo Redis ───────────────────────────────────────
  // Resolución O(1) en catélogo oculto -> read-through Redis/SQL ->
  // captura en buffer Redis. Sin GET por escaneo ni localStorage.
  const handleColectorScanRedis = async (codigo) => {
    origenScannerRef.current = true;
    const texto = String(codigo || "").trim();
    if (!texto || funcionalidadBloqueada) return;
    const operadorMatch = texto.match(/^([+\-])(\d+)$/);
    if (operadorMatch && partidaSeleccionada && partidaSeleccionada.id) {
      const operador = operadorMatch[1];
      const cantidad = parseInt(operadorMatch[2], 10);
      if (!isNaN(cantidad) && cantidad > 0) {
        const cantidadActual = parseInt(partidaSeleccionada.cantidadarticulo, 10) || 0;
        const nuevaCantidad = operador === "+"
          ? cantidadActual + cantidad
          : Math.max(1, cantidadActual - cantidad);
        await handleActualizarCantidadPartidaEntrada(partidaSeleccionada, nuevaCantidad);
        refocusBarcode();
        return;
      }
    }
    let articulo = resolverArticuloCbarrasLocal(texto);
    if (!articulo) articulo = await handleResolverCodigoEntradas(texto);
    if (!articulo) {
      refocusBarcode();
      return;
    }
    ultimoArticuloRegistradoRef.current = {
      clavearticulo: articulo?.clavearticulo,
      medida: articulo?.medida,
      almacenexhibidor: almacenExhibidor,
    };
    // Pasar pcTrabajo como override para que el contexto use la PC correcta
    await handleCapturarEntradaRedis(articulo, 1, esEntradaDeOtraPC ? pcTrabajo : undefined);
    refocusBarcode();
  };

  // ─── Artículo seleccionado desde buscador ───────────────────────────────────
  const handleArticuloSeleccionado = async (data, almacenSeleccionadoModal) => {
    ultimoArticuloRegistradoRef.current = {
      clavearticulo: data?.clavearticulo,
      medida: data?.medida,
      almacenexhibidor: almacenSeleccionadoModal,
    };
    const articulo = {
      clavearticulo: data?.clavearticulo,
      descripcion: data?.descripcion,
      medida: data?.medida,
      factor: data?.factor || 1,
      pmvb: data?.pmvb || 0,
      costoventa: data?.costoventa || data?.preciodefinitivo || 0,
      codigobarras: data?.codigobarras || data?.codigoBarras || "",
    };
    await handleCapturarEntradaRedis(articulo, data?.cantidad || 1, esEntradaDeOtraPC ? pcTrabajo : undefined);
    setOpenModalBuscarArticulos(false);
    handleCloseBuscarArticuloDescripcion();
    refocusBarcode();
  };

  // ─── Eliminar partida/entrada ──────────────────────────────────────────────
  const handleEliminarData = async (title) => {
    if (title === "ELIMINAR PARTIDA") {
      await hendleEliminarPartidaEntradaSalida(partidaSeleccionada, "ENTRADA");
    } else if (title === "ELIMINAR ENTRADA") {
      await hendleEliminarEntradaSalida(folioEntrada, "TODA LA ENTRADA", esEntradaDeOtraPC ? pcTrabajo : undefined);
    }
    refocusBarcode();
  };

  // ─── Navigación entre filas de cantidad ────────────────────────────────────
  // ─── Reserva del pie fijo al desplazar: evita que filas/inputs queden tapados ──
  // Localiza el contenedor con scroll real (ancestros) en vez de asumir selectores.
  const desplazarElementoSobrePie = (el) => {
    if (!el) return;
    let scroller = null;
    let n = el.parentElement;
    while (n) {
      if (n.scrollHeight > n.clientHeight + 1) { scroller = n; break; }
      n = n.parentElement;
    }
    const footer = document.getElementById('fixed-footer-entsal');
    const reserva = (footer?.offsetHeight || 70) + 12;
    if (!scroller) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      return;
    }
    const sr = scroller.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const limiteInferior = sr.bottom - reserva;
    if (r.bottom > limiteInferior) {
      scroller.scrollTo({ top: scroller.scrollTop + (r.bottom - limiteInferior), behavior: 'smooth' });
    } else if (r.top < sr.top + 4) {
      scroller.scrollTo({ top: scroller.scrollTop - (sr.top - r.top + 4), behavior: 'smooth' });
    }
  };

  // ─── Navigación entre filas de cantidad (por índice visible, no por id) ──────
  const handleNavigateCantidad = useCallback((currentRow, direction = 1, indiceActual) => {
    const orden = ordenVisible();
    if (!orden || orden.length === 0) return;
    let pos = (typeof indiceActual === "number" && indiceActual >= 0 && indiceActual < orden.length)
      ? indiceActual
      : orden.findIndex((r) => r.id === currentRow?.id);
    if (pos < 0) pos = 0;

    let nextPos = pos + direction;
    if (nextPos < 0) nextPos = orden.length - 1;
    if (nextPos >= orden.length) nextPos = 0;
    posNavRef.current = nextPos;

    setTimeout(() => {
      // Foco por posición en el DOM (mismo orden visible), a prueba de ids duplicados
      const celdas = document.querySelectorAll('[data-cantidad-id]');
      const celda = celdas[nextPos];
      const cantidadInput = celda ? celda.querySelector('input') : null;
      if (cantidadInput) {
        cantidadInput.focus();
        cantidadInput.select();
        desplazarElementoSobrePie(cantidadInput);
      }
    }, 50);
  }, [listaEntradas]);

  const normalizarTexto = (value) => String(value ?? "").trim().toUpperCase();

  const normalizarMedida = (value) => {
    const numero = Number(value);
    return Number.isFinite(numero) ? String(numero) : normalizarTexto(value);
  };

  const enfocarCantidadPartida = useCallback((row) => {
    setTimeout(() => {
      const cantidadInput = document.querySelector(`[data-cantidad-id="${row.id}"] input`);
      if (cantidadInput) {
        cantidadInput.focus();
        cantidadInput.select();
        desplazarElementoSobrePie(cantidadInput);
      }
    }, 80);
  }, []);

  useEffect(() => {
    const ultimoArticulo = ultimoArticuloRegistradoRef.current;
    if (!ultimoArticulo || !Array.isArray(listaEntradas) || listaEntradas.length === 0) return;

    const claveRegistrada = normalizarTexto(ultimoArticulo.clavearticulo);
    const medidaRegistrada = normalizarMedida(ultimoArticulo.medida);
    const almacenRegistrado = normalizarTexto(ultimoArticulo.almacenexhibidor);

    const partidaRegistrada = listaEntradas.find((row) => {
      const mismaClave = normalizarTexto(row.clavearticulo || row.sClaveArticulo) === claveRegistrada;
      const mismaMedida = normalizarMedida(row.medida || row.dMedida) === medidaRegistrada;
      const mismoAlmacen = !almacenRegistrado ||
        normalizarTexto(row.almacenexhibidor || row.sAlmacenExhibidor) === almacenRegistrado;

      return mismaClave && mismaMedida && mismoAlmacen;
    });

    if (!partidaRegistrada) return;

    setPartidaSeleccionada(partidaRegistrada);
    // Sincroniza el índice de navegación con el registro nuevo/sumado
    const listaActual = Array.isArray(listaEntradas) ? listaEntradas : [];
    const idxReg = listaActual.findIndex((r) => r.id === partidaRegistrada.id);
    if (idxReg >= 0) posNavRef.current = idxReg;
    setTimeout(() => {
      const bcInput = document.querySelector('#txtCodigoBarras');
      if (bcInput) bcInput.focus();
    }, 0);
    ultimoArticuloRegistradoRef.current = null;
  }, [listaEntradas, setPartidaSeleccionada, enfocarCantidadPartida]);

  useEffect(() => {
    if (origenScannerRef.current) {
      origenScannerRef.current = false;
      return;
    }
    if (titleActionSave === "EDITAR" && Array.isArray(listaEntradas) && listaEntradas.length > 0) {
      //enfocarCantidadPartida(listaEntradas[0]);
    }
  }, [titleActionSave, listaEntradas, enfocarCantidadPartida]);

  const desplazarFilaSeleccionada = () => {
    const selectedRow = tableRef.current?.querySelector('tr.row');
    if (!selectedRow) return;
    desplazarElementoSobrePie(selectedRow);
  };

  useEffect(() => {
    if (!partidaSeleccionada?.id) return;
    requestAnimationFrame(desplazarFilaSeleccionada);
    const timer = setTimeout(desplazarFilaSeleccionada, 150);
    return () => clearTimeout(timer);
  }, [partidaSeleccionada]);

  // ─── Wrappers de acciones con PC de trabajo ─────────────────────────────────
  const handleAplicarEntradaWrapper = async () => {
    if (!(await guardarEntradaSiHayCambios())) return;
    await handleAplicarEntrada(esEntradaDeOtraPC ? pcTrabajo : undefined);
    setPcTrabajo(getPcAnfitriona());
  };

  const handleImprimirEntradaWrapper = async () => {
    if (!(await guardarEntradaSiHayCambios())) return;
    await handleImprimirEntrada(esEntradaDeOtraPC ? pcTrabajo : undefined);
  };

  const handleNuevaEntradaWrapper = async () => {
    if (!(await guardarEntradaSiHayCambios())) return;
    nuevaEntradaEnProcesoRef.current = true;
    try {
      await handleClickNuevaEntradaButton();
    } finally {
      nuevaEntradaEnProcesoRef.current = false;
    }
    refocusBarcode();
  };

  // ─── Rev 9 (23/09/2026): finado Redis -> SQL ───────────────────────────────
  // Un solo flush antes de aplicar/imprimir/nueva/salir/cambio de folio.
  // El backend toma las partidas del buffer; el frontend solo manda cabecero.
  const guardarEntradaSiHayCambios = useCallback(async () => {
    if (movimientoBloqueado || titleActionSave !== "GUARDAR") return true;
    if (!listaEntradas || listaEntradas.length === 0) return true;
    try {
      return await handleFlushEntradaRedis(esEntradaDeOtraPC ? pcTrabajo : undefined);
    } catch {
      return false;
    }
  }, [movimientoBloqueado, titleActionSave, handleFlushEntradaRedis, esEntradaDeOtraPC, pcTrabajo, listaEntradas]);

  // ─── Autoguardado al salir de la ventana (desmontaje) ───────────────────────
  // Si quedan capturas sin guardar, se envían al backend para no perderlas.
  // Es no-op cuando no hay pendientes o no esté en edición. TR/CA nunca se tocan.
  const guardarAlSalirRef = useRef(null);
  guardarAlSalirRef.current = guardarEntradaSiHayCambios;
  useEffect(() => () => {
    try {
      const p = guardarAlSalirRef.current?.();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {}
  }, []);

  const handleSalirWrapper = async () => {
    await guardarEntradaSiHayCambios();
    handleClickRegresarMainDashboard();
  };

  // ─── beforeunload: guardar al cerrar pestaña/navegador ─────────────────────
  useEffect(() => {
    const advertirPendientes = (e) => {
      if (movimientoBloqueado || titleActionSave !== "GUARDAR") return;
      const items = EntradasLocalService.cargar();
      if (!items || items.length === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", advertirPendientes);
    return () => window.removeEventListener("beforeunload", advertirPendientes);
  }, [movimientoBloqueado, titleActionSave]);

  // ─── Rev 10 (23/09/2026): autofinado periódico silencioso ─────────────────
  // Cada 45s fina el buffer en SQL sin loader ni recarga (el estado en
  // pantalla ya es la mezcla exacta). No interrumpe el escaneo.
  const flushSilenciosoRef = useRef(null);
  flushSilenciosoRef.current = () => {
    if (movimientoBloqueado || titleActionSave !== "GUARDAR") return Promise.resolve(true);
    if (!listaEntradas || listaEntradas.length === 0) return Promise.resolve(true);
    return handleFlushEntradaRedis(esEntradaDeOtraPC ? pcTrabajo : undefined, { silent: true }).catch(() => false);
  };
  useEffect(() => {
    const timer = setInterval(() => {
      try { flushSilenciosoRef.current?.(); } catch {}
    }, 45000);
    return () => clearInterval(timer);
  }, []);

  // ─── Rev 10: finado al cerrar la pestaña (keepalive, best-effort) ─────────
  // El buffer Redis persiste 72h de todos modos; esto solo adelanta el finado.
  useEffect(() => {
    const persistirAlOcultar = () => {
      if (movimientoBloqueado || titleActionSave !== "GUARDAR") return;
      if (!listaEntradas || listaEntradas.length === 0) return;
      try {
        const token = localStorage.getItem("sizesoftx-tenant-access-token") || "";
        if (!token || !folioEntrada) return;
        fetch(apiEntradasGuardarTemporales, {
          method: "POST",
          headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
          body: JSON.stringify({
            folio: folioEntrada,
            concepto: conceptoEntrada || "",
            almacenexhibidor: almacenExhibidor || "",
            pc: String(esEntradaDeOtraPC ? pcTrabajo : (parametrosCajaForm.caja || "")).trim().toUpperCase(),
            usuario: "",
            items: [],
          }),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    };
    window.addEventListener("pagehide", persistirAlOcultar);
    return () => window.removeEventListener("pagehide", persistirAlOcultar);
  }, [movimientoBloqueado, titleActionSave, listaEntradas, folioEntrada, conceptoEntrada, almacenExhibidor, esEntradaDeOtraPC, pcTrabajo, parametrosCajaForm.caja]);

  // ─── Helpers UI ─────────────────────────────────────────────────────────────
  const refocusBarcode = useCallback(() => {
    setTimeout(() => {
      const bcInput = document.querySelector('#txtCodigoBarras');
      if (bcInput) { bcInput.focus(); bcInput.select(); }
    }, 0);
  }, []);

  const handleRowClick = (row) => {
    setPartidaSeleccionada(row);
    const orden = ordenVisible();
    const idx = orden.findIndex((item) => item.id === row?.id);
    if (idx >= 0) posNavRef.current = idx;
    refocusBarcode();
  };

  const handleEliminaPartida = async () => {
    await hendleEliminarPartidaEntradaSalida(partidaSeleccionada, "ENTRADA");
    refocusBarcode();
  };
  const handleEliminaEntrada = async () => {
    await hendleEliminarEntradaSalida(folioEntrada, "TODA LA ENTRADA", esEntradaDeOtraPC ? pcTrabajo : undefined);
    refocusBarcode();
  };

  // Rev 11 (23/09/2026): foco al filtro con reintentos (el portal del modal
  // puede montar tarde) + manejador único para Alt+F1 y botón Artículos:
  // - TR/CA: cierra el buscador.
  // - Ya abierto: lo trae al frente con focus+select (repetible con cada Alt+F1).
  // - Cerrado: abre solo si no esté bloqueado.
  const enfocarFiltroConReintentos = () => {
    let intentos = 0;
    const timer = setInterval(() => {
      const filtroInput = document.querySelector('input[name="Filtro"]');
      if (filtroInput) {
        filtroInput.focus();
        if (typeof filtroInput.select === "function") filtroInput.select();
        clearInterval(timer);
      } else if (++intentos > 12) {
        clearInterval(timer);
      }
    }, 60);
  };

    const manejarBuscadorArticulos = () => {
    if (movimientoBloqueado) {
      setOpenModalBuscarArticulos(false);
      return;
    }
    if (mostrarBuscadorArticulosRef.current) {
      enfocarFiltroConReintentos();
      return;
    }
    if (buscadorArticulosBloqueado) return;
    setOpenModalBuscarArticulos(true);
    handleBuscarArticuloDescripcion();
  };


  const abrirBuscadorArticulos = () => {
    manejarBuscadorArticulos();
  };

    useEffect(() => {
    mostrarBuscadorArticulosRef.current = openModalBuscarArticulos;
    if (openModalBuscarArticulos) {
      enfocarFiltroConReintentos();
    }
  }, [openModalBuscarArticulos]);


  // Rev 11: si el movimiento se termina/cancela con el buscador abierto,
  // se cierra el estado para que no quede "abierto pero invisible".
  useEffect(() => {
    if (movimientoBloqueado && openModalBuscarArticulos) {
      setOpenModalBuscarArticulos(false);
    }
  }, [movimientoBloqueado]);

  // ─── Atajos de teclado ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!event.key) return;
      const key = String(event.key).trim().toUpperCase();

      if (key === "F4") {
        event.preventDefault();
        event.stopPropagation();
        handleClickRegresarMainDashboard();
        return;
      }

      if (event.altKey && key.trim().toUpperCase() === "F1") {
        event.preventDefault();
        event.stopPropagation();
        manejarBuscadorArticulos();
        return;
      }

      if (event.altKey && key.trim().toUpperCase() === "A") {
        event.preventDefault();
        event.stopPropagation();
        if (puedeAplicar) handleAplicarEntradaWrapper();
        return;
      }

      if (event.altKey && key.trim().toUpperCase() === "I") {
        event.preventDefault();
        event.stopPropagation();
        if (tieneRegistros && !movimientoBloqueado) handleImprimirEntradaWrapper();
        return;
      }

      if (event.altKey && key.trim().toUpperCase() === "F12") {
        event.preventDefault();
        event.stopPropagation();
        if (tieneRegistros) setOpenConfirmEliminarEntrada(true);
        return;
      }

      const target = event.target;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isInput) return;

      const orden = ordenVisible();
      if (!orden || orden.length === 0) return;
      let pos = posNavRef.current;
      if (pos < 0 || pos >= orden.length) {
        const idx = orden.findIndex(item => partidaSeleccionada && item.id === partidaSeleccionada.id);
        pos = idx >= 0 ? idx : 0;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        let newPos = pos;
        if (event.key === 'ArrowDown') {
          newPos = pos < orden.length - 1 ? pos + 1 : pos;
        } else {
          newPos = pos > 0 ? pos - 1 : 0;
        }
        posNavRef.current = newPos;
        if (orden[newPos]) {
          setPartidaSeleccionada(orden[newPos]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [movimientoBloqueado, buscadorArticulosBloqueado, tieneRegistros, puedeAplicar, pcTrabajo, parametrosCajaForm.caja, listaEntradas, partidaSeleccionada]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Fragment>
        <SizeSoftXLayoutForm
          titlePage="Módulo de entradas Almacenes/Exhibidores"
          buttonActions={
            <MnuEntradasAlmacenesExhibidoresActions
              onOpenBuscador={abrirBuscadorArticulos}
              onImprimir={handleImprimirEntradaWrapper}
              onAplicar={handleAplicarEntradaWrapper}
              onEliminarPartida={handleEliminaPartida}
              onEliminarEntrada={handleEliminaEntrada}
              buscadorArticulosBloqueado={buscadorArticulosBloqueado}
              onRefocusBarcode={refocusBarcode}
              onNueva={handleNuevaEntradaWrapper}
              onSalir={handleSalirWrapper}
              accionesBloqueadas={funcionalidadBloqueada}
            />
          }
        >
          <div className="conteiner-form-general">
            <div className="conteiner-form-left">
              <div className="body-form">
                <TextField autoComplete="off"
                  name="folioentrada"
                  label="Folio Entrada:"
                  value={folioEntrada ? folioEntrada : 0}
                  sx={{ width: "500px", height: "6.3vh" }}
                  disabled
                />
                <TextField autoComplete="off"
                  name="fechaentrada"
                  label="Fecha:"
                  value={
                    listaEntradas.length > 0
                      ? listaEntradas[0].fecharegistro != null
                        ? getDateStringV2(listaEntradas[0].fecharegistro)
                        : getDateStringV2(Date())
                      : getDateStringV2(Date())
                  }
                  sx={{ width: "500px", height: "6.3vh" }}
                  disabled
                />
                <SemaforoStatus
                  status={
                    listaEntradas.length > 0
                      ? listaEntradas[0].statusentradasalida
                      : "PE"
                  }
                />
                {esEntradaDeOtraPC && (
                  <TextField autoComplete="off"
                    name="pcOrigen"
                    label="PC Origen:"
                    value={pcTrabajo}
                    sx={{ width: "200px", height: "6.3vh" }}
                    disabled
                    color="warning"
                    focused
                  />
                )}
              </div>

              <div className="body-form">
                <Autocomplete
                  freeSolo
                  noOptionsText="Sin resultados"
                  openOnFocus
                  id="id-concepto-entrada"
                  options={listaConceptosAutoComplete}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.concepto
                  }
                  value={conceptoSeleccionado}
                  inputValue={conceptoInput}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setConceptoSeleccionado(null);
                      setConceptoInput(newValue);
                      handleChangeConcepto(event, newValue);
                      return;
                    }
                    if (newValue && typeof newValue === "object") {
                      setConceptoSeleccionado(newValue);
                      setConceptoInput(newValue.concepto);
                      handleChangeConcepto(event, newValue.concepto);
                      return;
                    }
                    setConceptoSeleccionado(null);
                    setConceptoInput("");
                    handleChangeConcepto(event, "");
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setConceptoSeleccionado(null);
                      setConceptoInput(newInputValue);
                      handleChangeConcepto(event, newInputValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Concepto entrada:" required />
                  )}
                  sx={{ width: "500px", height: "6.3vh" }}
                />

                <Autocomplete
                  freeSolo
                  noOptionsText="Sin resultados"
                  openOnFocus
                  id="id-almacen-exhibidor"
                  options={listaAlmacenesExhibidoresAutoComplete}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.nombre
                  }
                  value={almacenSeleccionado}
                  inputValue={almacenInput}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setAlmacenSeleccionado(null);
                      setAlmacenInput(newValue);
                      const norm = newValue.trim().toUpperCase();
                      const valid = listaAlmacenesExhibidoresAutoComplete.some(
                        (item) => item.nombre.trim().toUpperCase() === norm
                      );
                      setAlmacenError(!valid && newValue.length > 0);
                      handleChangeAlmacenexhibidor(event, newValue);
                      return;
                    }
                    if (newValue && typeof newValue === "object") {
                      setAlmacenSeleccionado(newValue);
                      setAlmacenInput(newValue.nombre);
                      setAlmacenError(false);
                      handleChangeAlmacenexhibidor(event, newValue.nombre);
                      return;
                    }
                    setAlmacenSeleccionado(null);
                    setAlmacenInput("");
                    setAlmacenError(false);
                    handleChangeAlmacenexhibidor(event, "");
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setAlmacenInput(newInputValue);
                      const norm = newInputValue.trim().toUpperCase();
                      const valid = listaAlmacenesExhibidoresAutoComplete.some(
                        (item) => item.nombre.trim().toUpperCase() === norm
                      );
                      setAlmacenError(!valid && newInputValue.length > 0);
                      handleChangeAlmacenexhibidor(event, newInputValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Almacén o Exhibidor:"
                      required
                      error={almacenError}
                      helperText={almacenError ? "Seleccione un almacén vélido de la lista" : ""}
                    />
                  )}
                  sx={{ width: "300px", height: "6.3vh" }}
                />
              </div>

              <div className="body-Detalles-entradas-salidas">
                {/* La tabla queda 1.5cm més chica que su contenedor para librar el pie fijo */}
                <div ref={tableRef} style={{ paddingBottom: '1cm' }}>
                  <SizeSoftXTableVentas
                    columns={[
                      { title: "Clave artículo", field: "clavearticulo", render: (row) => (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {row.clavearticulo}
                          <IconButton size="small" title="Copiar código de barras" aria-label="Copiar código de barras" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); copiarTexto(row.codigobarras || row.codigoBarras || row.clavearticulo); }}>
                            <ContentCopy fontSize="inherit" sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      ) },
                      { title: "Descripción", field: "descripcion" },
                      { title: "Medida", field: "medida" },
                      {
                        title: "Cantidad",
                        field: "cantidadarticulo",
                        render: (row, indice) => (
                          <CantidadCell
                            key={`qty-${row.id}-${row.cantidadarticulo}`}
                            row={row}
                            indice={indice}
                            disabled={estaPartidaBloqueada(row)}
                            onSave={handleActualizarCantidadPartidaEntrada}
                            onNavigate={handleNavigateCantidad}
                          />
                        ),
                      },
                      { title: "Almacen/Exhibidor", field: "almacenexhibidor" },
                      { title: "PMVB", field: "pmvb" },
                      { title: "P. Definitivo", field: "costoventa" },
                    ]}
                    data={listaEntradas ?? []}
                    id="id"
                    row={partidaSeleccionada}
                    setRow={handleRowClick}
                    fontSize="small"
                    disablePagination
                    onSortChange={aplicarOrdenVisible}
                  />
                </div>
              </div>

            </div>
          </div>
        </SizeSoftXLayoutForm>

        <Box
          id="fixed-footer-entsal"
          sx={{
            position: 'fixed',
            bottom: '10px',
            left: '20px',
            right: '20px',
            borderRadius: '12px',
            zIndex: 1100,
            backgroundColor: '#fff',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 2,
            px: 3,
            py: '8px',
            borderTop: '1px solid #ddd',
          }}
        >
          <Box sx={{ flex: '1 1 auto' }}>
            <SizeSoftXLectorCodigoBarrasEntradas
              onColectorScan={handleColectorScanRedis}
              disabled={lectorCodigoBarrasBloqueado}
            />
          </Box>
          <TextField autoComplete="off"
            label="Total registros"
            value={totalesLocal.registros}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ flex: '0 0 130px' }}
          />
          <TextField autoComplete="off"
            label="Cantidad artículos"
            value={totalesLocal.articulos}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ flex: '0 0 130px' }}
          />
          <TextField autoComplete="off"
            label="Total PMVB"
            value={totalesLocal.pmvb}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ flex: '0 0 130px' }}
          />
          {(titleOpenDialog === "Módulo de salidas Almacenes/Exhibidores" || tipoUsuario === "ADMINISTRADOR") && (
            <TextField autoComplete="off"
              label="Costo venta"
              value={totalCostoVenta}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ flex: '0 0 130px' }}
            />
          )}
          {(titleOpenDialog === "Módulo de salidas Almacenes/Exhibidores" || tipoUsuario === "ADMINISTRADOR") && (
            <TextField autoComplete="off"
              label="Venta neta"
              value={totalVentaNeta}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ flex: '0 0 130px' }}
            />
          )}
        </Box>

        <ModalBuscarArticulosParaVenta
          openModal={openModalBuscarArticulos && !movimientoBloqueado}
          handleCloseModal={() => {
            setOpenModalBuscarArticulos(false);
            handleCloseBuscarArticuloDescripcion();
            refocusBarcode();
          }}
          dominio={almacenExhibidor || parametrosCajaForm.dominio}
          consultarTodosAlmacenes={true}
          onTallaSelect={(e) => setTalla(e)}
          articuloSelect={handleArticuloSeleccionado}
        />

        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              width: "80%",
              height: "80vh",
              margin: "auto",
              background: "white",
              padding: 2,
            }}
          >
            {pdfUrl && <iframe src={pdfUrl} width="100%" height="100%" />}
          </Box>
        </Modal>

        <SizeSoftXConfirmDialog
          open={statusOpenDialog}
          title={titleOpenDialog}
          message={messageOpenDialog}
          handleConfirmDialog={() => handleEliminarData(titleOpenDialog)}
        />

        <SizeSoftXConfirmDialog
          open={openConfirmEliminarEntrada}
          title="Eliminar todo"
          message="Se guardaré la entrada actual y se limpiaré la captura. ¿Desea continuar?"
          handleConfirmDialog={(confirm) => {
            setOpenConfirmEliminarEntrada(false);
            if (confirm) handleNuevaEntradaWrapper();
          }}
        />

        <ModalBuscarEntradasSalidas
          tipo={"ENTRADA"}
          openModal={openModarlBuscarEntradaSalida}
          handleCloseModal={() => { handleCloseModarBuscarEntradaSalida(); refocusBarcode(); }}
          onSelectEntrada={handleSelectEntradaConValidacionPC}
        />

        <SizeSoftXAlert />

        <SizeSoftXLoadingProcess
          open={statusWaiting}
          message="Procesando información, por favor espere..."
        />
      </Fragment>
    </>
  );
}
